import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import styles from "../SustainQueueScreenStyles";
import { SustainQueueRow, TicketStatus, TicketUrgency } from "../../../store/slices/ticketingPortalSlice";
import { TableFilters, EMPTY_TABLE_FILTERS } from "../hooks/useSustainQueueListing";
import { formatDateWithTimezone } from "../util/dateFormatter";

// ── Types ─────────────────────────────────────────────────────────────────────

type SortKey =
  | "id" | "category" | "subcategory" | "submitter"
  | "location" | "system" | "urgency" | "status"
  | "assignee" | "aging" | "submitted";

type SortDir = "asc" | "desc";

type FilterOptions = {
  categories: string[];
  subcategories: string[];
  locations: string[];
  markets: string[];
  regions: string[];
  tertiaries: string[];
};

type ManagerOption = { email: string; label: string };

type Props = {
  query: string;
  totalCount: number;
  filteredTickets: SustainQueueRow[];
  assignableUsers: string[];
  filterOptions: FilterOptions;
  managerOptions: ManagerOption[];
  tableFilters: TableFilters;
  onTableFiltersChange: (f: TableFilters) => void;
  onQueryChange: (value: string) => void;
  onRowPress?: (ticketId: string) => void;
  onQuickEdit: (ticketId: string, updates: Partial<Pick<SustainQueueRow, "urgency" | "status" | "assignedTo">>) => void;
  onDuplicateBadgePress?: (ticketId: string) => void;
  isReadOnly?: boolean;
  /** Current page number (1-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Number of items per page */
  pageSize: number;
  /** Called when the user navigates to a different page */
  onPageChange: (page: number) => void;
  /**
   * When set, the table scrolls to the row with this ticket ID after rendering.
   * Used to restore position when returning from Ticket Detail.
   */
  scrollToTicketId?: string | null;
  isDark?: boolean;
  colors?: any;
};

// ── Constants / helpers ───────────────────────────────────────────────────────

const URGENCY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const STATUS_ORDER: Record<string, number> = {
  new: 0, progress: 1, "waiting-it": 2, "waiting-tmv": 3,
  "submitted-ado": 4, "waiting-user": 5, resolved: 6,
};

const URGENCY_OPTIONS: TicketUrgency[] = ["critical", "high", "medium", "low"];
const URGENCY_LABELS: Record<string, string> = {
  critical: "Critical", high: "High", medium: "Medium", low: "Low",
};
const URGENCY_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: "#FFE6E4", text: "#C23603", dot: "#C23603" },
  high:     { bg: "#FFE9D1", text: "#C56F12", dot: "#D0781D" },
  medium:   { bg: "#FFF6CC", text: "#8B6508", dot: "#8B6508" },
  low:      { bg: "#F3F4F6", text: "#6B7280", dot: "#6B7280" },
};
const URGENCY_COLORS_DARK: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: "#4A1A14", text: "#FF8A78", dot: "#FF8A78" },
  high:     { bg: "#3D2608", text: "#FFAD5C", dot: "#FFAD5C" },
  medium:   { bg: "#3A2E12", text: "#FFD966", dot: "#FFD966" },
  low:      { bg: "#2B2B2B", text: "#9CA3AF", dot: "#9CA3AF" },
};

const STATUS_OPTIONS: TicketStatus[] = [
  "new", "progress", "waiting-it", "waiting-tmv",
  "submitted-ado", "waiting-user", "resolved",
];
const STATUS_LABELS: Record<string, string> = {
  new: "New", progress: "In Progress", "waiting-it": "Escalated to IT",
  "waiting-user": "Waiting on User", "waiting-tmv": "Escalated to TMV",
  "submitted-ado": "Submitted to ADO", resolved: "Closed",
};
const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  new:            { bg: "#DCE8FB", text: "#005CBC", dot: "#005CBC" },
  progress:       { bg: "#E4E0FD", text: "#5C4EC9", dot: "#7871E2" },
  "waiting-it":   { bg: "#FFF6CC", text: "#8B6508", dot: "#FCC42B" },
  "waiting-tmv":  { bg: "#FFF6CC", text: "#8B6508", dot: "#FCC42B" },
  "submitted-ado":{ bg: "#FFE9D1", text: "#D0781D", dot: "#FF8C09" },
  "waiting-user": { bg: "#FFF6CC", text: "#8B6508", dot: "#FCC42B" },
  resolved:       { bg: "#F3F4F6", text: "#6B7280", dot: "#6B7280" },
};
const STATUS_COLORS_DARK: Record<string, { bg: string; text: string; dot: string }> = {
  new:            { bg: "#1B2A3D", text: "#6AADFF", dot: "#6AADFF" },
  progress:       { bg: "#26204A", text: "#A89EFF", dot: "#A89EFF" },
  "waiting-it":   { bg: "#3A2E12", text: "#FFD966", dot: "#FCC42B" },
  "waiting-tmv":  { bg: "#3A2E12", text: "#FFD966", dot: "#FCC42B" },
  "submitted-ado":{ bg: "#3D2608", text: "#FFAD5C", dot: "#FF8C09" },
  "waiting-user": { bg: "#3A2E12", text: "#FFD966", dot: "#FCC42B" },
  resolved:       { bg: "#2B2B2B", text: "#9CA3AF", dot: "#9CA3AF" },
};

/**
 * Parse a UTC timestamp from the API that may or may not carry a timezone suffix.
 * Backends often omit the trailing 'Z', causing JS to treat the value as local time
 * (which adds the client's UTC offset — e.g. +05:30 for IST — to every elapsed calc).
 * Always appending 'Z' forces UTC interpretation and gives the correct elapsed duration.
 */
const toUtcMs = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  // If the string already has a timezone indicator (Z or ±HH:MM) leave it as-is
  const normalized = /[Zz]$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const ms = new Date(normalized).getTime();
  return isNaN(ms) ? 0 : ms;
};

const fmtItTime = (ms: number): string => {
  if (!ms) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
};

/** Format the Active Resolution Timer (ms → human string) */
const fmtActiveTime = (ms: number): string => {
  if (ms <= 0) return "< 1m";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) { const m = mins % 60; return m > 0 ? `${hours}h ${m}m` : `${hours}h`; }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h > 0 ? `${days}d ${h}h` : `${days}d`;
};

// ── Portal overlay (escapes all RN ScrollView overflow) ───────────────────────

interface PortalMenuProps {
  triggerId: string;
  menuId: string;
  onClose: () => void;
  children: React.ReactNode;
  isDark?: boolean;
}
const PortalMenu: React.FC<PortalMenuProps> = ({ triggerId, menuId, onClose, children, isDark }) => {
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 160 });

  useEffect(() => {
    const el = document.getElementById(triggerId);
    if (el) {
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, minWidth: Math.max(160, r.width) });
    }
  }, [triggerId]);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const trigger = document.getElementById(triggerId);
      const menu = document.getElementById(menuId);
      const t = e.target as Node;
      if ((!trigger || !trigger.contains(t)) && (!menu || !menu.contains(t))) onClose();
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [triggerId, menuId, onClose]);

  return createPortal(
    <div
      id={menuId}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        minWidth: pos.minWidth,
        background: isDark ? "#2B2B2B" : "#ffffff",
        border: isDark ? "1px solid #404040" : "1px solid #e5e7eb",
        borderRadius: 8,
        boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.45)" : "0 4px 16px rgba(0,0,0,0.14)",
        zIndex: 99999,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </div>,
    document.body
  );
};

// ── Filter multi-select dropdown ──────────────────────────────────────────────

interface FilterMultiSelectProps {
  label: string;
  options: string[];
  optionLabels?: Record<string, string>;
  selected: string[];
  onChange: (v: string[]) => void;
  isDark?: boolean;
}
const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  label, options, optionLabels, selected, onChange, isDark,
}) => {
  const [open, setOpen] = useState(false);
  const id = `tbl-f-${label.replace(/[^a-z0-9]/gi, "")}`;
  const menuId = `${id}-menu`;

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);

  const display =
    selected.length === 0 ? label
    : selected.length === 1 ? (optionLabels?.[selected[0]] ?? selected[0])
    : `${label} (${selected.length})`;

  return (
    <View style={s.filterWrap}>
      <Pressable
        // @ts-ignore web id
        id={id} nativeID={id}
        style={[
          s.filterTrigger,
          isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" },
          selected.length > 0 && s.filterTriggerActive,
          selected.length > 0 && isDark && { backgroundColor: "#1B2A3D", borderColor: "#3D8BFF" },
        ]}
        onPress={() => setOpen(v => !v)}
      >
        <Text style={[
          s.filterTriggerText,
          isDark && { color: "#B0B0B0" },
          selected.length > 0 && s.filterTriggerTextActive,
          selected.length > 0 && isDark && { color: "#3D8BFF" },
        ]} numberOfLines={1}>
          {display}
        </Text>
        {selected.length > 0 ? (
          <Pressable onPress={(e) => { (e as any).stopPropagation?.(); onChange([]); }} hitSlop={6}>
            <MaterialIcons name="close" size={12} color={isDark ? "#3D8BFF" : "#005CBC"} />
          </Pressable>
        ) : (
          <MaterialIcons name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"} size={15} color={isDark ? "#B0B0B0" : "#585858"} />
        )}
      </Pressable>

      {open && Platform.OS === "web" && (
        <PortalMenu triggerId={id} menuId={menuId} onClose={() => setOpen(false)} isDark={isDark}>
          <div style={{ maxHeight: 170, overflowY: "auto" }}>
            {options.map(opt => {
              const isSel = selected.includes(opt);
              const lbl = optionLabels?.[opt] ?? opt;
              return (
                <div
                  key={opt}
                  onMouseDown={(e) => { e.preventDefault(); toggle(opt); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 12px", cursor: "pointer",
                    background: isSel ? (isDark ? "#1B2A3D" : "#eff6ff") : "transparent",
                    fontSize: 13, color: isSel ? (isDark ? "#3D8BFF" : "#005CBC") : (isDark ? "#F2F2F2" : "#1f2937"),
                    fontFamily: "inherit", userSelect: "none",
                  }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                    border: isSel ? "none" : (isDark ? "1.5px solid #6E6E6E" : "1.5px solid #d1d5db"),
                    background: isSel ? (isDark ? "#3D8BFF" : "#005CBC") : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isSel && (
                      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                        <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  {lbl}
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div
              onMouseDown={(e) => { e.preventDefault(); onChange([]); }}
              style={{
                padding: "7px 12px",
                borderTop: isDark ? "1px solid #404040" : "1px solid #f3f4f6",
                color: isDark ? "#3D8BFF" : "#005CBC",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "right" as const,
                userSelect: "none" as const,
                background: isDark ? "#202020" : "#f9fafb",
              }}
            >
              Clear all
            </div>
          )}
        </PortalMenu>
      )}
    </View>
  );
};

// ── Inline cell dropdown (Urgency / Status / Assigned To) ─────────────────────

interface InlineCellProps {
  triggerId: string;
  menuId: string;
  currentValue: string;
  options: { value: string; label: string; colors?: { bg: string; text: string; dot: string } }[];
  onSelect: (v: string) => void;
  onClose: () => void;
  isDark?: boolean;
}
const InlineCellDropdown: React.FC<InlineCellProps> = ({
  triggerId, menuId, currentValue, options, onSelect, onClose, isDark,
}) => (
  <PortalMenu triggerId={triggerId} menuId={menuId} onClose={onClose} isDark={isDark}>
    <div style={{ minWidth: 190, maxHeight: 170, overflowY: "auto" }}>
      {options.map(opt => {
        const isCurrent = opt.value === currentValue;
        return (
          <div
            key={opt.value}
            onMouseDown={(e) => { e.preventDefault(); onSelect(opt.value); onClose(); }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
              cursor: "pointer",
              background: isCurrent
                ? (isDark ? "#1B2A3D" : "#eff6ff")
                : "transparent",
              fontFamily: "inherit", userSelect: "none" as const,
            }}
          >
            {opt.colors ? (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: opt.colors.bg, borderRadius: 100,
                padding: "2px 8px", fontSize: 12, fontWeight: 600, color: opt.colors.text,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: opt.colors.dot, display: "inline-block" }} />
                {opt.label}
              </span>
            ) : (
              <span style={{ fontSize: 13, color: isCurrent ? (isDark ? "#6AADFF" : "#005CBC") : (isDark ? "#F2F2F2" : "#1f2937") }}>{opt.label}</span>
            )}
            {isCurrent && (
              <span style={{ marginLeft: "auto" }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7L5.5 10.5L12 3.5" stroke={isDark ? "#6AADFF" : "#005CBC"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </div>
        );
      })}
    </div>
  </PortalMenu>
);

// ── Sort header ───────────────────────────────────────────────────────────────

interface SortHeaderProps {
  colKey: SortKey;
  label: string;
  currentKey: SortKey;
  currentDir: SortDir;
  colStyle: object;
  onSort: (k: SortKey) => void;
  isDark?: boolean;
}
const SortHeader: React.FC<SortHeaderProps> = ({ colKey, label, currentKey, currentDir, colStyle, onSort, isDark }) => {
  const active = currentKey === colKey;
  return (
    <Pressable style={[styles.thCell, styles.thSortable, colStyle]} onPress={() => onSort(colKey)}>
      <Text style={[styles.thText, isDark && { color: "#B0B0B0" }, active && styles.thTextActive, active && isDark && { color: "#3D8BFF" }]}>{label}</Text>
      <MaterialIcons
        name={active ? (currentDir === "asc" ? "arrow-upward" : "arrow-downward") : "unfold-more"}
        size={12}
        color={active ? (isDark ? "#3D8BFF" : "#005CBC") : (isDark ? "#6E6E6E" : "#aab3c2")}
      />
    </Pressable>
  );
};

// ── Export to CSV ─────────────────────────────────────────────────────────────

const exportToCSV = (tickets: SustainQueueRow[]) => {
  const now = Date.now();
  const headers = [
    "Navi Ticket ID", "Category", "Sub-Category", "Submitter",
    "Location/Region/Market", "Ticket Level", "Impact", "Urgency",
    "Status", "Assigned To", "Time in Queue", "Submitted",
  ];
  const rows = tickets.map(t => {
    // ── Live timer calculation: time in queue = createdAt → now ──
    const liveActiveMs = Math.max(0, now - toUtcMs(t.submitted));

    const agingDisplay = fmtActiveTime(liveActiveMs);

    return [
      t.id,
      t.category,
      t.subcategory ?? "-",
      t.submittedBy ?? "-",
      t.location,
      t.ticketLevel ?? "-",
      `${t.impactSites ?? 0} ${(t.impactSites ?? 0) === 1 ? "site" : "sites"}`,
      URGENCY_LABELS[t.urgency] ?? t.urgency,
      STATUS_LABELS[t.status] ?? t.status,
      t.assignedTo || "-",
      agingDisplay,
      formatDateWithTimezone(t.submitted),
    ];
  });

  // Prepend UTF-8 BOM (\ufeff) and separator configuration (sep=,) so Excel correctly groups cells in columns.
  const csvContent = "\ufeff" + "sep=,\n" + [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell ?? "").replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sustain-queue-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── Main component ────────────────────────────────────────────────────────────

const SustainQueueTable: React.FC<Props> = ({
  query,
  totalCount,
  filteredTickets,
  assignableUsers,
  filterOptions,
  managerOptions,
  tableFilters,
  onTableFiltersChange,
  onQueryChange,
  onRowPress,
  onQuickEdit,
  onDuplicateBadgePress,
  isReadOnly = false,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  scrollToTicketId,
  isDark,
  colors: _colors,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>("submitted");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  // Live clock — ticks every 30 s to keep timer display in sync
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Inline cell open state: { ticketId, field }
  const [inlineOpen, setInlineOpen] = useState<{ ticketId: string; field: "urgency" | "status" | "assignee" } | null>(null);

  const setFilter = useCallback(<K extends keyof TableFilters>(key: K, val: TableFilters[K]) =>
    onTableFiltersChange({ ...tableFilters, [key]: val }), [tableFilters, onTableFiltersChange]);

  const clearAllFilters = () => onTableFiltersChange(EMPTY_TABLE_FILTERS);

  const hasActiveFilters = Object.values(tableFilters).some(v => v.length > 0);

  const onSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  // Combined location/market/region options
  const locationOptions = useMemo(() => {
    const all = new Set([
      ...filterOptions.locations,
      ...filterOptions.markets,
      ...filterOptions.regions,
    ]);
    return [...all].sort();
  }, [filterOptions]);

  // Apply table-level filters on top of the already query-filtered tickets
  const tableFiltered = useMemo(() => {
    return filteredTickets.filter(t => {
      if (tableFilters.statuses.length > 0 && !tableFilters.statuses.includes(t.status)) return false;
      if (tableFilters.urgencies.length > 0 && !tableFilters.urgencies.includes(t.urgency)) return false;
      if (tableFilters.categories.length > 0 && !tableFilters.categories.includes(t.category)) return false;
      if (tableFilters.subcategories.length > 0 && !tableFilters.subcategories.includes(t.subcategory ?? "")) return false;
      if (tableFilters.locations.length > 0) {
        const match = tableFilters.locations.some(v =>
          t.location === v || t.market === v || t.region === v
        );
        if (!match) return false;
      }
      if (tableFilters.assignees.length > 0 && !tableFilters.assignees.includes(t.assignedTo)) return false;
      return true;
    });
  }, [filteredTickets, tableFilters]);

  const sortedTickets = useMemo(() => {
    return [...tableFiltered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "id":          cmp = a.id.localeCompare(b.id); break;
        case "category":    cmp = a.category.localeCompare(b.category); break;
        case "subcategory": cmp = (a.subcategory ?? "").localeCompare(b.subcategory ?? ""); break;
        case "submitter":   cmp = (a.submittedBy ?? "").localeCompare(b.submittedBy ?? ""); break;
        case "location":    cmp = a.location.localeCompare(b.location); break;
        case "system":      cmp = (a.impactSites ?? 0) - (b.impactSites ?? 0); break;
        case "urgency":     cmp = (URGENCY_ORDER[a.urgency] ?? 99) - (URGENCY_ORDER[b.urgency] ?? 99); break;
        case "status":      cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99); break;
        case "assignee":    cmp = a.assignedTo.localeCompare(b.assignedTo); break;
        case "aging":
          // Sort by elapsed time (now - submitted). Ascending = least time in queue first
          // (most recently submitted). Uses toUtcMs() to normalise timezone-less strings
          // so parsing is consistent across all browsers.
          cmp = toUtcMs(b.submitted) - toUtcMs(a.submitted);
          break;
        case "submitted":
          cmp = toUtcMs(a.submitted) - toUtcMs(b.submitted);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [tableFiltered, sortKey, sortDir]);

  const sortProps = (key: SortKey, colStyle: object): SortHeaderProps => ({
    colKey: key, label: COL_LABELS[key], currentKey: sortKey,
    currentDir: sortDir, colStyle, onSort, isDark,
  });

  const openInline = useCallback((e: any, ticketId: string, field: "urgency" | "status" | "assignee") => {
    e?.stopPropagation?.();
    setInlineOpen(prev =>
      prev?.ticketId === ticketId && prev?.field === field ? null : { ticketId, field }
    );
  }, []);

  const closeInline = useCallback(() => setInlineOpen(null), []);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 0) return [];
    const maxButtons = 5;
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - maxButtons + 1));
    const end = Math.min(totalPages, start + maxButtons - 1);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentPage, totalPages]);

  // ── Scroll-to-ticket restoration ─────────────────────────────────────────
  // rowRefs maps ticketId → row View ref for native measureLayout.
  const rowRefs = useRef<Record<string, View | null>>({});
  const outerScrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (!scrollToTicketId || sortedTickets.length === 0) return;
    const timer = setTimeout(() => {
      if (Platform.OS === "web") {
        try {
          const el = (globalThis as any).document?.getElementById(
            `sq-ticket-row-${scrollToTicketId}`
          );
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch {
          // Graceful no-op
        }
      } else {
        const rowRef = rowRefs.current[scrollToTicketId];
        const scrollView = outerScrollRef.current as any;
        if (rowRef && scrollView) {
          rowRef.measureLayout(
            scrollView,
            (_x: number, y: number, _w: number, h: number) => {
              scrollView.scrollTo({ y: Math.max(0, y - h), animated: true });
            },
            () => { /* graceful no-op */ }
          );
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [scrollToTicketId, sortedTickets]);

  // ── Dual (top + bottom) horizontal scrollbar sync ─────────────────────────
  const tableScrollRef = useRef<ScrollView>(null);
  const topScrollBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const top = topScrollBarRef.current;
    const bottom = (tableScrollRef.current as any)?.getScrollableNode?.() as HTMLElement | null;
    if (!top || !bottom) return;
    let syncing = false;
    const fromTop = () => { if (syncing) return; syncing = true; bottom.scrollLeft = top.scrollLeft; syncing = false; };
    const fromBottom = () => { if (syncing) return; syncing = true; top.scrollLeft = bottom.scrollLeft; syncing = false; };
    top.addEventListener("scroll", fromTop, { passive: true });
    bottom.addEventListener("scroll", fromBottom, { passive: true });
    return () => {
      top.removeEventListener("scroll", fromTop);
      bottom.removeEventListener("scroll", fromBottom);
    };
  }, []);

  return (
    <View style={[styles.dataTableWrap, isDark && { backgroundColor: "#202020" }]}>
      {/* ── Toolbar: search + export ── */}
      <View style={[styles.tableToolbar, isDark && { borderBottomColor: "#363636" }]}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={14} color={isDark ? "#6E6E6E" : "#818181"} style={styles.searchIcon} />
          <TextInput
            style={[styles.searchInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" } as any]}
            value={query}
            onChangeText={onQueryChange}
            placeholder="Search Ticket ID, Issue Summary, Submitter"
            placeholderTextColor={isDark ? "#6E6E6E" : "#9099aa"}
          />
          {query.length > 0 && (
            <Pressable onPress={() => onQueryChange("")} hitSlop={8} style={styles.searchClearBtn}>
              <MaterialIcons name="close" size={13} color={isDark ? "#6E6E6E" : "#818181"} />
            </Pressable>
          )}
        </View>
        <Text style={[styles.resultCount, isDark && { color: "#B0B0B0" }]}>{sortedTickets.length} tickets</Text>
        {Platform.OS === "web" && (
          <Pressable style={[s.exportBtn, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]} onPress={() => exportToCSV(sortedTickets)}>
            <MaterialIcons name="download" size={14} color={isDark ? "#3D8BFF" : "#005CBC"} />
            <Text style={[s.exportBtnText, isDark && { color: "#3D8BFF" }]}>Export</Text>
          </Pressable>
        )}
      </View>

      {/* ── Filter bar ── */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[s.filterBar, isDark && { borderBottomColor: "#363636" }]}
        keyboardShouldPersistTaps="handled"
      >
        <FilterMultiSelect
          label="Status"
          isDark={isDark}
          options={STATUS_OPTIONS}
          optionLabels={STATUS_LABELS}
          selected={tableFilters.statuses}
          onChange={v => setFilter("statuses", v)}
        />
        <FilterMultiSelect
          label="Urgency"
          isDark={isDark}
          options={URGENCY_OPTIONS}
          optionLabels={URGENCY_LABELS}
          selected={tableFilters.urgencies}
          onChange={v => setFilter("urgencies", v)}
        />
        <FilterMultiSelect
          label="Category"
          isDark={isDark}
          options={filterOptions.categories}
          selected={tableFilters.categories}
          onChange={v => setFilter("categories", v)}
        />
        <FilterMultiSelect
          label="Sub-Category"
          isDark={isDark}
          options={filterOptions.subcategories}
          selected={tableFilters.subcategories}
          onChange={v => setFilter("subcategories", v)}
        />
        <FilterMultiSelect
          label="Tertiary"
          isDark={isDark}
          options={filterOptions.tertiaries}
          selected={tableFilters.tertiaries}
          onChange={v => setFilter("tertiaries", v)}
        />
        <FilterMultiSelect
          label="Location / Market / Region"
          isDark={isDark}
          options={locationOptions}
          selected={tableFilters.locations}
          onChange={v => setFilter("locations", v)}
        />
        <FilterMultiSelect
          label="Assigned To"
          isDark={isDark}
          options={assignableUsers}
          selected={tableFilters.assignees}
          onChange={v => setFilter("assignees", v)}
        />
        <FilterMultiSelect
          label="Manager"
          isDark={isDark}
          options={managerOptions.map((m) => m.email)}
          optionLabels={Object.fromEntries(managerOptions.map((m) => [m.email, m.label]))}
          selected={tableFilters.managers}
          onChange={v => setFilter("managers", v)}
        />
        {hasActiveFilters && (
          <Pressable style={s.clearFiltersBtn} onPress={clearAllFilters}>
            <MaterialIcons name="filter-alt-off" size={13} color="#dc2626" />
            <Text style={s.clearFiltersBtnText}>Clear filters</Text>
          </Pressable>
        )}
      </ScrollView>

      {Platform.OS === "web" && React.createElement("div", {
        ref: topScrollBarRef,
        style: { overflowX: "scroll", overflowY: "hidden", height: 14 },
      }, React.createElement("div", { style: { width: 1760, height: 1 } }))}

      {/* ── Table ── */}
      <ScrollView ref={tableScrollRef} horizontal showsHorizontalScrollIndicator>
        <View style={styles.tableMinWidth}>
          {/* Header */}
          <View style={[styles.tableHeadRow, isDark && { backgroundColor: "#1a1a1a", borderBottomColor: "#404040" }]}>
            <SortHeader {...sortProps("id", styles.colId)} />
            <SortHeader {...sortProps("category", styles.colCategory)} />
            <SortHeader {...sortProps("subcategory", styles.colSubcategory)} />
            <SortHeader {...sortProps("submitter", styles.colSubmitter)} />
            <SortHeader {...sortProps("location", styles.colLocation)} />
            <SortHeader {...sortProps("system", styles.colSystem)} />
            <SortHeader {...sortProps("urgency", styles.colUrgency)} />
            <SortHeader {...sortProps("status", styles.colStatus)} />
            <SortHeader {...sortProps("assignee", styles.colAssigned)} />
            <SortHeader {...sortProps("aging", styles.colAging)} />
            <SortHeader {...sortProps("submitted", styles.colSubmitted)} />
          </View>

          {/* Empty States */}
          {sortedTickets.length === 0 && (
            <View style={styles.emptyRow}>
              {totalCount === 0 && !query.trim() && !hasActiveFilters ? (
                // No tickets exist at all (no search, no filters)
                <>
                  <MaterialIcons name="inbox" size={36} color={isDark ? "#404040" : "#c0c8d4"} style={{ marginBottom: 12 }} />
                  <Text style={[styles.emptyRowText, isDark && { color: "#B0B0B0" }, { fontSize: 15, fontWeight: "600", marginBottom: 6 }]}>
                    No tickets have been submitted yet.
                  </Text>
                  <Text style={[styles.emptyRowText, { fontSize: 13, color: isDark ? "#6E6E6E" : "#818181" }]}>
                    Tickets will appear here once they are submitted to the Sustain Queue.
                  </Text>
                </>
              ) : (
                // Search or filters returned no results
                <>
                  <MaterialIcons name="search-off" size={36} color={isDark ? "#404040" : "#c0c8d4"} style={{ marginBottom: 12 }} />
                  <Text style={[styles.emptyRowText, isDark && { color: "#B0B0B0" }, { fontSize: 15, fontWeight: "600", marginBottom: 6 }]}>
                    No tickets match the selected filters
                  </Text>
                  <Text style={[styles.emptyRowText, { fontSize: 13, color: isDark ? "#6E6E6E" : "#818181" }]}>
                    Try adjusting your search or filter criteria.
                  </Text>
                </>
              )}
            </View>
          )}

          {sortedTickets.map((ticket) => {
            const urgencyLabel = URGENCY_LABELS[ticket.urgency] ?? "Low";
            const statusLabel = STATUS_LABELS[ticket.status] ?? "New";
            const uc = isDark
              ? (URGENCY_COLORS_DARK[ticket.urgency] ?? URGENCY_COLORS[ticket.urgency])
              : URGENCY_COLORS[ticket.urgency];
            const sc = isDark
              ? (STATUS_COLORS_DARK[ticket.status] ?? STATUS_COLORS[ticket.status])
              : STATUS_COLORS[ticket.status];
            const hovered = hoveredRowId === ticket.id;

            // ── Live timer calculation ──────────────────────────────────────
            // Time in Queue = from createdAt (ticket.submitted) to now — toUtcMs
            // normalises strings that lack a timezone suffix so they're parsed as UTC
            // rather than local time (which would add the client's offset, e.g. +05:30).
            const liveActiveMs = Math.max(0, now - toUtcMs(ticket.submitted));
            // Waiting on IT = from waitingOnItTimerISO to now (only while in waiting-it status)
            const liveWaitingItMs = (ticket.status === "waiting-it" && ticket.waitingOnItTimerISO)
              ? Math.max(0, now - toUtcMs(ticket.waitingOnItTimerISO))
              : 0;

            const agingDisplay = fmtActiveTime(liveActiveMs);
            const itTimeDisplay = fmtItTime(liveWaitingItMs);
            const showItTimer = liveWaitingItMs > 0;

            const urgencyTriggerId = `inline-${ticket.id}-urgency`;
            const statusTriggerId = `inline-${ticket.id}-status`;
            const assigneeTriggerId = `inline-${ticket.id}-assignee`;
            const urgencyMenuId = `${urgencyTriggerId}-menu`;
            const statusMenuId = `${statusTriggerId}-menu`;
            const assigneeMenuId = `${assigneeTriggerId}-menu`;

            const urgencyOpen = inlineOpen?.ticketId === ticket.id && inlineOpen?.field === "urgency";
            const statusOpen = inlineOpen?.ticketId === ticket.id && inlineOpen?.field === "status";
            const assigneeOpen = inlineOpen?.ticketId === ticket.id && inlineOpen?.field === "assignee";

            // Debug: Log duplicate badge data for specific tickets
            if (ticket.id.includes("00319") || ticket.id.includes("00311") || ticket.hasDuplicate || (ticket.possibleDuplicateCount ?? 0) > 0) {
              console.log(`[SustainQueueTable] Ticket ${ticket.id} duplicate check:`, {
                hasDuplicate: ticket.hasDuplicate,
                possibleDuplicateCount: ticket.possibleDuplicateCount,
                shouldShowBadge: ticket.hasDuplicate || (ticket.possibleDuplicateCount ?? 0) > 0
              });
            }

            const rowNativeId = `sq-ticket-row-${ticket.id}`;

            return (
              <Pressable
                key={ticket.id}
                // nativeID / id: used by scrollIntoView (web) and measureLayout (native)
                // @ts-ignore — id is valid on React Native Web; nativeID on native
                id={rowNativeId}
                nativeID={rowNativeId}
                ref={(el) => { rowRefs.current[ticket.id] = el as View | null; }}
                // @ts-ignore web events
                onMouseEnter={() => setHoveredRowId(ticket.id)}
                onMouseLeave={() => setHoveredRowId(null)}
                style={({ pressed }) => [
                  styles.tableBodyRow,
                  isDark && { backgroundColor: "#202020", borderBottomColor: "#2B2B2B" },
                  hovered && styles.tableBodyRowHovered,
                  hovered && isDark && { backgroundColor: "#252525" },
                  pressed && styles.tableBodyRowPressed,
                  pressed && isDark && { backgroundColor: "#2B2B2B" },
                ]}
                onPress={() => onRowPress?.(ticket.id)}
              >
                {/* Navi Ticket ID + Duplicate Badge */}
                <View style={[styles.tdCellWrap, styles.colId, { flexDirection: "row", alignItems: "center", gap: 6 }]}>
                  <Text style={[styles.tdId, isDark && { color: "#3D8BFF" }]}>{ticket.id}</Text>
                  {/* Duplicate badge – non-clickable for read-only (non-sustain-team) users */}
                  {(ticket.hasDuplicate || (ticket.possibleDuplicateCount ?? 0) > 0) && (
                    Platform.OS === "web" ? React.createElement(
                      "div",
                      {
                        title: isReadOnly
                          ? "Similar tickets detected."
                          : "Similar tickets detected. Review for potential merge.",
                        style: { display: "flex", alignItems: "center", justifyContent: "center" },
                      },
                      <Pressable
                        onPress={isReadOnly ? undefined : (e) => {
                          (e as any).stopPropagation?.();
                          onDuplicateBadgePress?.(ticket.id);
                        }}
                        style={[s.dupBadgeButton, isReadOnly && s.dupBadgeButtonReadOnly]}
                        hitSlop={8}
                      >
                        <Text style={s.dupBadgeText}>!</Text>
                      </Pressable>
                    ) : (
                      <Pressable
                        onPress={isReadOnly ? undefined : (e) => {
                          (e as any).stopPropagation?.();
                          onDuplicateBadgePress?.(ticket.id);
                        }}
                        style={[s.dupBadgeButton, isReadOnly && s.dupBadgeButtonReadOnly]}
                        hitSlop={8}
                      >
                        <Text style={s.dupBadgeText}>!</Text>
                      </Pressable>
                    )
                  )}
                </View>

                {/* Category */}
                <Text style={[styles.tdCell, isDark && { color: "#F2F2F2" }, styles.colCategory]} numberOfLines={2}>{ticket.category}</Text>

                {/* Sub-Category */}
                <Text style={[styles.tdCell, isDark && { color: "#B0B0B0" }, styles.colSubcategory]} numberOfLines={2}>
                  {ticket.subcategory ?? "—"}
                </Text>

                {/* Submitter */}
                <Text style={[styles.tdCell, isDark && { color: "#B0B0B0" }, styles.colSubmitter]} numberOfLines={1}>
                  {ticket.submittedBy ?? "—"}
                </Text>

                {/* Location / Region / Market */}
                <View style={[styles.tdCellWrap, styles.colLocation]}>
                  <Text style={[styles.locationPrimary, isDark && { color: "#F2F2F2" }]} numberOfLines={1}>{ticket.location}</Text>
                  {ticket.ticketLevel && (
                    <Text style={[styles.locationSecondary, isDark && { color: "#6E6E6E" }]}>{ticket.ticketLevel}</Text>
                  )}
                </View>

                {/* Impact */}
                <View style={[styles.tdCellWrap, styles.colSystem]}>
                  <Text style={[styles.tdCell, isDark && { color: "#B0B0B0" }]}>
                    {(ticket.impactSites ?? 0) === 1
                      ? "1 site"
                      : `${ticket.impactSites ?? 0} sites`}
                  </Text>
                </View>

                {/* Urgency — inline editable */}
                <View style={[styles.tdCellWrap, styles.colUrgency]}>
                  <Pressable
                    // @ts-ignore web id
                    id={urgencyTriggerId} nativeID={urgencyTriggerId}
                    style={[s.inlineBadge, { backgroundColor: uc?.bg ?? "#f0f0f0" }, urgencyOpen && s.inlineBadgeOpen]}
                    onPress={(e) => openInline(e, ticket.id, "urgency")}
                  >
                    <View style={[styles.badgeDot, { backgroundColor: uc?.dot ?? "#888" }]} />
                    <Text style={[styles.badgeText, { color: uc?.text ?? "#333" }]}>{urgencyLabel}</Text>
                    <MaterialIcons name="keyboard-arrow-down" size={12} color={uc?.text ?? "#333"} />
                  </Pressable>
                  {urgencyOpen && Platform.OS === "web" && (
                    <InlineCellDropdown
                      triggerId={urgencyTriggerId}
                      menuId={urgencyMenuId}
                      currentValue={ticket.urgency}
                      options={URGENCY_OPTIONS.map(u => ({
                        value: u, label: URGENCY_LABELS[u],
                        colors: isDark ? URGENCY_COLORS_DARK[u] : URGENCY_COLORS[u],
                      }))}
                      onSelect={(v) => onQuickEdit(ticket.id, { urgency: v as TicketUrgency })}
                      onClose={closeInline}
                      isDark={isDark}
                    />
                  )}
                </View>

                {/* Status — inline editable */}
                <View style={[styles.tdCellWrap, styles.colStatus]}>
                  <Pressable
                    // @ts-ignore web id
                    id={statusTriggerId} nativeID={statusTriggerId}
                    style={[s.inlineBadge, { backgroundColor: sc?.bg ?? "#f0f0f0" }, statusOpen && s.inlineBadgeOpen]}
                    onPress={(e) => openInline(e, ticket.id, "status")}
                  >
                    <View style={[styles.badgeDot, { backgroundColor: sc?.dot ?? "#888" }]} />
                    <Text style={[styles.badgeText, { color: sc?.text ?? "#333" }]}>{statusLabel}</Text>
                    <MaterialIcons name="keyboard-arrow-down" size={12} color={sc?.text ?? "#333"} />
                  </Pressable>
                  {statusOpen && Platform.OS === "web" && (
                    <InlineCellDropdown
                      triggerId={statusTriggerId}
                      menuId={statusMenuId}
                      currentValue={ticket.status}
                      options={STATUS_OPTIONS.map(st => ({
                        value: st, label: STATUS_LABELS[st],
                        colors: isDark ? STATUS_COLORS_DARK[st] : STATUS_COLORS[st],
                      }))}
                      onSelect={(v) => onQuickEdit(ticket.id, { status: v as TicketStatus })}
                      onClose={closeInline}
                      isDark={isDark}
                    />
                  )}
                </View>

                {/* Assigned To — inline editable */}
                <View style={[styles.tdCellWrap, styles.colAssigned]}>
                  <Pressable
                    // @ts-ignore web id
                    id={assigneeTriggerId} nativeID={assigneeTriggerId}
                    style={[
                      s.inlineAssignee,
                      assigneeOpen && s.inlineAssigneeOpen,
                      assigneeOpen && isDark && { backgroundColor: "#1B2A3D", borderColor: "#3D8BFF" },
                    ]}
                    onPress={(e) => openInline(e, ticket.id, "assignee")}
                  >
                    <Text style={[s.inlineAssigneeText, isDark && { color: "#F2F2F2" }]} numberOfLines={1}>{ticket.assignedTo}</Text>
                    <MaterialIcons name="keyboard-arrow-down" size={13} color={isDark ? "#B0B0B0" : "#585858"} />
                  </Pressable>
                  {assigneeOpen && Platform.OS === "web" && (
                    <InlineCellDropdown
                      triggerId={assigneeTriggerId}
                      menuId={assigneeMenuId}
                      currentValue={ticket.assignedTo}
                      options={assignableUsers.filter(u => u !== "Unassigned").map(u => ({ value: u, label: u }))}
                      onSelect={(v) => onQuickEdit(ticket.id, { assignedTo: v })}
                      onClose={closeInline}
                      isDark={isDark}
                    />
                  )}
                </View>

                {/* Time in Queue */}
                <View style={[styles.tdCellWrap, styles.colAging, styles.agingCell]}>
                  <Text style={[styles.tdCell, isDark && { color: "#B0B0B0" }]}>{agingDisplay}</Text>
                  {showItTimer && (
                    <Text style={styles.agingSecondary}>Waiting on IT: {itTimeDisplay}</Text>
                  )}
                </View>

                {/* Submitted */}
                <Text style={[styles.tdCell, isDark && { color: "#B0B0B0" }, styles.colSubmitted, styles.tdDate]}>
                  {formatDateWithTimezone(ticket.submitted)}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>

      {/* Pagination footer (My Tickets style, fixed 100 rows) */}
      {!totalCount ? null : (
        <View style={[styles.pagination, isDark && { borderTopColor: "#363636" }]}>
          <Text style={[styles.paginationText, isDark && { color: "#B0B0B0" }]}>
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount} tickets
          </Text>
          <View style={s.paginationGroup}>
            <Pressable
              disabled={currentPage <= 1}
              style={[currentPage <= 1 ? s.pgButtonDisabled : s.pgButton, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}
              onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
            >
              <MaterialIcons name="chevron-left" size={14} color={currentPage <= 1 ? "#6E6E6E" : (isDark ? "#3D8BFF" : "#034895")} />
            </Pressable>

            {visiblePageNumbers.map((pageNumber) => {
              const isActive = pageNumber === currentPage;
              return (
                <Pressable
                  key={pageNumber}
                  style={[isActive ? s.pgButtonActive : s.pgButton, isDark && !isActive && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}
                  onPress={() => !isActive && onPageChange(pageNumber)}
                >
                  <Text style={[isActive ? s.pgButtonActiveText : s.pgButtonText, isDark && !isActive && { color: "#3D8BFF" }]}>{pageNumber}</Text>
                </Pressable>
              );
            })}

            <Pressable
              disabled={currentPage >= totalPages}
              style={[currentPage >= totalPages ? s.pgButtonDisabled : s.pgButton, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}
              onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
            >
              <MaterialIcons name="chevron-right" size={14} color={currentPage >= totalPages ? "#6E6E6E" : (isDark ? "#3D8BFF" : "#034895")} />
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );
};

const COL_LABELS: Record<SortKey, string> = {
  id: "Navi Ticket ID",
  category: "Category",
  subcategory: "Sub-Category",
  submitter: "Submitter",
  location: "Location / Region / Market",
  system: "Impact",
  urgency: "Urgency Level",
  status: "Status",
  assignee: "Assigned To",
  aging: "Time in Queue",
  submitted: "Submitted",
};

// ── Local styles ──────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  filterBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#ebebeb",
  },
  filterWrap: { position: "relative" },
  filterTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "#ffffff",
    maxWidth: 180,
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  filterTriggerActive: { borderColor: "#005CBC", backgroundColor: "#eff6ff" },
  filterTriggerText: { fontSize: 12, color: "#3a3a3a", flex: 1 },
  filterTriggerTextActive: { color: "#005CBC", fontWeight: "600" },
  clearFiltersBtn: {
    flexDirection: "row", alignItems: "center", gap: 5,
    paddingHorizontal: 10, paddingVertical: 6,
    borderWidth: 1, borderColor: "#fca5a5", borderRadius: 6,
    backgroundColor: "#fff5f5",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  clearFiltersBtnText: { fontSize: 12, color: "#dc2626", fontWeight: "600" },
  exportBtn: {
    flexDirection: "row", alignItems: "center", gap: 5,
    borderWidth: 1, borderColor: "#d0d0d0", borderRadius: 6,
    paddingHorizontal: 12, paddingVertical: 7,
    backgroundColor: "#ffffff",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  exportBtnText: { fontSize: 12, color: "#005CBC", fontWeight: "600" },
  // Inline editable cells
  inlineBadge: {
    flexDirection: "row", alignItems: "center", gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 100,
    alignSelf: "flex-start",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  inlineBadgeOpen: { boxShadow: "0 0 0 2px #005CBC" } as any,
  inlineAssignee: {
    flexDirection: "row", alignItems: "center", gap: 4,
    paddingHorizontal: 6, paddingVertical: 4, borderRadius: 4,
    borderWidth: 1, borderColor: "transparent",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  inlineAssigneeOpen: { borderColor: "#005CBC", backgroundColor: "#eff6ff" },
  inlineAssigneeText: { fontSize: 13, color: "#3a3a3a", flex: 1 },
  dupBadgeButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#FBBF24",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    borderWidth: 1,
    borderColor: "#FBBF24",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  dupBadgeButtonReadOnly: {
    opacity: 0.6,
    ...(Platform.OS === "web" ? { cursor: "default" } as any : {}),
  },
  dupBadgeText: {
    color: "#000000",
    fontWeight: "900",
    fontSize: 12,
    textAlign: "center",
    lineHeight: 14,
  },
  // ── Pagination controls ───────────────────────────────────────────────────
  paginationGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  pgButtonDisabled: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.5,
  },
  pgButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#034895",
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  pgButtonText: {
    color: "#034895",
    fontSize: 12,
    fontWeight: "600" as const,
  },
  pgButtonActive: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: "#034895",
    alignItems: "center",
    justifyContent: "center",
  },
  pgButtonActiveText: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "700" as const,
  },
});

export default SustainQueueTable;
